import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "w-full flex" }
const _hoisted_2 = {
  key: 0,
  class: "seats flex flex-wrap align-content-middle gap-3"
}
const _hoisted_3 = {
  key: 0,
  class: "alert-red font-bold"
}
const _hoisted_4 = {
  key: 1,
  class: "alert-orange font-bold"
}
const _hoisted_5 = { class: "ml-auto" }
const _hoisted_6 = { class: "search-input mb-3 flex" }
const _hoisted_7 = ["textContent"]
const _hoisted_8 = { class: "flex flex-row" }
const _hoisted_9 = { class: "ml-3" }
const _hoisted_10 = { class: "font-semibold mb-1" }

import { computed, onMounted, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Chip from 'primevue/chip';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import TransferModal from '@/components/Modal/TransferModal/TransferModal.vue';
import SeatsInUseModal from './SeatsInUseModal.vue';
import { useArchiveStore, useOrganizationStore, useStudentStore, useOverageStore } from '@/store';
import { OrganizationInterface, StudentTableInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentTable',
  setup(__props) {

const studentStore = useStudentStore();
const archiveStore = useArchiveStore();
const overageStore = useOverageStore();
const organization = useOrganizationStore().organization;
const students = ref<StudentTableInterface[]>([]);
const selectedStudents = ref<StudentTableInterface[]>([]);
const studentCount = computed(() => studentStore.totalStudents);
const studentModalOpen = ref<boolean>(false);
const transferModalOpen = ref<boolean>(false);
const selectedTransferOrganization = ref<OrganizationInterface>();
const archivedVisible = ref<boolean>(false);
const filterName = ref<string>('');
const showSeats = ref<boolean>(false);

onMounted(async () => {
  if (can([PERMISSIONS.ORGANIZATION_STUDENTS_INDEX])) {
    await getStudents();

    await getOverages(new Date());
  }
  selectedStudents.value = [];
});

const getOverages = async (date: Date) => {
  if (can([PERMISSIONS.OVERAGES_LIST])) {
    await overageStore.getOrganizationOverages(organization.id, date, true);
  }
};

const getStudents: (search?: string) => Promise<void> = async (search?: string) => {
  let filter = '?';
  if (search) filter += `search=${search}&`;
  if (archivedVisible.value) filter += 'archived=1&';

  const studentData = await studentStore.getStudentsForTable(filter).catch((e) => {
    students.value = [];
    throw e;
  });
  students.value = studentData;
};

const archiveStudents: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  const archived_reason_id = 1;
  await archiveStore.archive(id, { archived_reason_id }).then(() => {
    archiveStore.doToast(`${student} has been  archived`, 'Archived');
    getStudents();
  });
};

const unArchiveStudents: (id: number, student: string) => Promise<void> = async (id: number, student: string) => {
  await archiveStore.unarchive(id).then(() => {
    archiveStore.doToast(`${student} has been  unarchived`, 'Unarchived');
    getStudents();
  });
};

const attachStudents: (ids: number[]) => Promise<void> = async (ids) => {
  await studentStore
    .attachStudents(ids)
    .then(() => {
      studentStore.doToast('Student(s) has been added', 'Added');

      studentStore.getTotalStudents();
    })
    .catch(() => {
      studentStore.doToast('Student(s) could not be added', 'Error', true);
    });

  await getStudents();
};

const detachStudents: (ids: number[], studentName: string) => Promise<void> = async (ids, studentName) => {
  await studentStore.detachStudents(ids);
  getStudents();
  studentStore.doToast(`${studentName} has been removed from organization`, 'Removed');
};

const createMenuModel = (item: StudentTableInterface) => {
  const menuItems = [
    {
      label: 'Remove Student',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_STUDENTS]),
      command: () => {
        detachStudents([item.id], item.name);
      },
    },
    {
      label: 'Archive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: () => archiveStudents(item.id, item.name),
    },
    {
      label: 'Transfer Student',
      visible: can([PERMISSIONS.ORGANIZATION_TRANSFER_STUDENTS]),
      command: () => {
        selectedStudents.value = [item];
        openTransferModal();
      },
    },
  ];
  if (item.archived) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_UNARCHIVE]),
      command: async () => unArchiveStudents(item.id, item.name),
    });
  }
  return menuItems;
};

const openAddStudents = async () => {
  studentModalOpen.value = true;
  studentStore.page = 0;
  await studentStore.getAddStudentsModalData();
};

const onLoadMoreStudents = async () => {
  await studentStore.getAddStudentsModalData();
};

const onSearch = () => {
  const search = filterName.value;
  if (search.length > 3) getStudents(search);

  if (search.length === 0) getStudents();
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  let studentIds = students.map((student) => {
    return student.value;
  });
  attachStudents(studentIds);
  studentModalOpen.value = false;
};

const openTransferModal = () => (transferModalOpen.value = true);

const onCancelTransfer = () => (transferModalOpen.value = false);

const onTransferStudents = async (payload: { students: number[]; organization: OrganizationInterface }) => {
  const { students, organization } = payload;
  selectedTransferOrganization.value = organization;
  await studentStore
    .transferStudents(students, organization.id as number)
    .then(() => {
      studentStore.doToast(`${selectedTransferOrganization.value?.name} Transferred`, 'Student Transferred');
    })
    .catch((errorResponse: { message?: string; errors: Record<string, string[]> }) => {
      const errorArr = ['Student transfer failed.', errorResponse.data.message];
      const detail: string = errorArr.join('\n');
      studentStore.doToast(detail, 'Transfer unsuccessful', true);
    });
  await getStudents();
  selectedStudents.value = [];
};

const showArchived = () => {
  archivedVisible.value = true;
  studentStore.showArchived = true;
  getStudents();
};

const hideArchived = () => {
  archivedVisible.value = false;
  studentStore.showArchived = false;
  getStudents();
};

const toggleSeatsInUseModal = () => {
  showSeats.value = !showSeats.value;
};

const seatsScheduledExceeded = computed(() => overageStore.scheduled_over);
const seatsCompletedExceeded = computed(() => overageStore.completed_over);

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_Toast = _resolveComponent("Toast")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showSeats.value)
      ? (_openBlock(), _createBlock(SeatsInUseModal, {
          key: 0,
          "organization-id": _unref(organization).id,
          onCancel: toggleSeatsInUseModal
        }, null, 8, ["organization-id"]))
      : _createCommentVNode("", true),
    _createVNode(_component_Card, null, {
      header: _withCtx(() => [
        _cache[3] || (_cache[3] = _createElementVNode("h2", null, "Active Students", -1)),
        _createVNode(_unref(Chip), null, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(studentCount.value), 1)
          ]),
          _: 1
        }),
        (_unref(can)([_unref(PERMISSIONS).OVERAGES_LIST]))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              (_unref(overageStore).show_seats_modal)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "font-bold cursor-pointer align-content-middle gap-2 flex",
                    onClick: toggleSeatsInUseModal
                  }, [
                    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "seats-button" }, "See Seats In Use", -1)),
                    (seatsCompletedExceeded.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_3, " You've exceeded your seats for the month, which may incur fees "))
                      : (seatsScheduledExceeded.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_4, " You are approaching your max seat count for the month. Exceeding this may incur a fee. "))
                        : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_ATTACH_STUDENTS]))
            ? (_openBlock(), _createBlock(_component_AppButton, {
                key: 0,
                type: "text",
                size: "small",
                text: "Add Students",
                "icon-left": "add",
                onClick: openAddStudents
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_component_IconField, { "icon-position": "left" }, {
            default: _withCtx(() => [
              _createVNode(_component_InputIcon, null, {
                default: _withCtx(() => [
                  _createVNode(_component_Icon, {
                    name: "search",
                    class: "text-lg"
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_InputText, {
                modelValue: filterName.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filterName).value = $event)),
                class: "p-inputtext-sm mr-2",
                placeholder: "Search students",
                onInput: onSearch
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_STUDENT_SHOW_ARCHIVE]))
            ? (_openBlock(), _createBlock(_unref(ArchiveSwitch), {
                key: 0,
                onEnable: showArchived,
                onDisable: hideArchived
              }))
            : _createCommentVNode("", true),
          (_unref(can)([_unref(PERMISSIONS).ORGANIZATION_TRANSFER_STUDENTS]))
            ? (_openBlock(), _createBlock(_component_AppButton, {
                key: 1,
                class: "ml-auto",
                type: "secondary",
                text: "Transfer Organization",
                disabled: !selectedStudents.value.length,
                onClick: openTransferModal
              }, null, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ]),
        _createVNode(_unref(DataTable), {
          selection: selectedStudents.value,
          "onUpdate:selection": _cache[1] || (_cache[1] = ($event: any) => ((selectedStudents).value = $event)),
          value: students.value,
          "row-class": ({ archived }) => (archived ? 'grey-row' : null),
          scrollable: true,
          "loading-icon": "logo-spinner",
          loading: _unref(studentStore).loading,
          "data-key": "name",
          "responsive-layout": "scroll",
          "scroll-height": "440px",
          class: _normalizeClass({ 'table-loading': _unref(studentStore).loading })
        }, {
          empty: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode("Students not assigned", -1)
          ]))]),
          default: _withCtx(() => [
            (students.value.length)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(_unref(Column), {
                    "selection-mode": "multiple",
                    "header-style": "justify-content: center; max-width: 3rem;",
                    "body-style": "justify-content: center; max-width: 3rem;"
                  }),
                  (_unref(studentStore).showArchived)
                    ? (_openBlock(), _createBlock(_unref(Column), {
                        key: 0,
                        field: "archived",
                        "header-style": "justify-content: center; max-width: 1rem;",
                        "body-style": "justify-content: center; max-width: 1rem;"
                      }, {
                        body: _withCtx(({ data }) => [
                          (data.archived)
                            ? _withDirectives((_openBlock(), _createBlock(_component_Icon, {
                                key: 0,
                                name: "info",
                                class: "text-color-gray-dark text-base cursor-pointer"
                              }, null, 512)), [
                                [
                                  _directive_tooltip,
                                  {
                    value: 'Archived',
                    escape: true,
                    fitContent: true,
                  },
                                  void 0,
                                  { top: true }
                                ]
                              ])
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(Column), {
                    "body-style": "max-width: 22rem",
                    field: "name",
                    header: "Student Name",
                    "header-style": "max-width: 22rem",
                    sortable: ""
                  }, {
                    body: _withCtx(({ data }) => [
                      (_unref(can)([_unref(PERMISSIONS).PEOPLE_SHOW]))
                        ? (_openBlock(), _createBlock(_component_RouterLink, {
                            key: 0,
                            to: `/user/${data.id}`,
                            class: "font-semibold",
                            textContent: _toDisplayString(data.name)
                          }, null, 8, ["to", "textContent"]))
                        : (_openBlock(), _createElementBlock("span", {
                            key: 1,
                            class: "font-semibold",
                            textContent: _toDisplayString(data.name)
                          }, null, 8, _hoisted_7))
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(Column), {
                    field: "disciplines",
                    header: "Disciplines",
                    "body-style": "font-size: .8725rem;"
                  }),
                  _createVNode(_unref(Column), {
                    "body-style": "justify-content: center; font-size: .8725rem; max-width: 10rem;",
                    field: "grade",
                    header: "Grade",
                    "header-class": "justify-content-center mw-10rem"
                  }),
                  _createVNode(_unref(Column), {
                    "body-style": "justify-content: center; font-size: .8725rem; max-width: 10rem;",
                    field: "age",
                    header: "Age",
                    "header-class": "justify-content-center mw-10rem"
                  }),
                  _createVNode(_unref(Column), {
                    "body-style": "justify-content: center; font-size: .8725rem; max-width: 10rem;",
                    field: "notifications",
                    header: "Notifications",
                    "header-class": "justify-content-center mw-10rem"
                  }, {
                    body: _withCtx(({ data }) => [
                      (data.notifications)
                        ? (_openBlock(), _createBlock(_component_Icon, {
                            key: 0,
                            name: "notifications_active",
                            class: "text-md text-teal-500"
                          }))
                        : (_openBlock(), _createBlock(_component_Icon, {
                            key: 1,
                            name: "notifications_off",
                            class: "text-md alert-red"
                          }))
                    ]),
                    _: 1
                  }),
                  (
                _unref(can)([
                  _unref(PERMISSIONS).ORGANIZATION_DETACH_STUDENTS,
                  _unref(PERMISSIONS).PEOPLE_ARCHIVE,
                  _unref(PERMISSIONS).ORGANIZATION_TRANSFER_STUDENTS,
                ])
              )
                    ? (_openBlock(), _createBlock(_unref(Column), {
                        key: 1,
                        "body-style": "justify-content: center; font-size: .8725rem; max-width: 10rem;",
                        "header-class": "justify-content-center mw-10rem",
                        header: "Actions"
                      }, {
                        body: _withCtx(({ data }) => [
                          _createVNode(_component_ActionMenu, {
                            "menu-items": createMenuModel(data),
                            icon: "more_horiz"
                          }, null, 8, ["menu-items"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["selection", "value", "row-class", "loading", "class"]),
        (studentModalOpen.value)
          ? (_openBlock(), _createBlock(AddPersonModal, {
              key: 0,
              title: "Add students",
              items: _unref(studentStore).studentModalData,
              loading: _unref(studentStore).modalDataLoading,
              searchable: true,
              "button-text": "Add students",
              placeholder: "Search students",
              "person-type": "students",
              "list-header-text": "Student name",
              onAddItems: onSaveAdd,
              onCancel: onCancelAdd,
              onLoadMoreItems: onLoadMoreStudents
            }, null, 8, ["items", "loading"]))
          : _createCommentVNode("", true),
        (transferModalOpen.value)
          ? (_openBlock(), _createBlock(TransferModal, {
              key: 1,
              students: selectedStudents.value,
              organization: _unref(organization),
              onCancel: onCancelTransfer,
              onTransfer: onTransferStudents
            }, null, 8, ["students", "organization"]))
          : _createCommentVNode("", true),
        _createVNode(_component_Toast, {
          class: "transfer-success__toast w-20rem",
          position: "bottom-right",
          group: "transfer-success"
        }, {
          message: _withCtx((slotProps) => [
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_component_Icon, {
                class: "md-24 success__toast__icon",
                name: "check_circle"
              }),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("h3", _hoisted_10, _toDisplayString(slotProps.message.summary), 1),
                _createVNode(_component_RouterLink, {
                  to: `/organizations/${selectedTransferOrganization.value?.id}`,
                  class: "underline",
                  textContent: _toDisplayString(slotProps.message.detail)
                }, null, 8, ["to", "textContent"])
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})